import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/admin";

/**
 * PR-friendly subset of the investor brief: no per-user data, no engine
 * internals, no telemetry on failure modes. Just the headline numbers that
 * are safe to drop into a press email.
 */
function Stat({ label, value, sublabel, testid }) {
  return (
    <div data-testid={testid} className="border border-[var(--border)] px-6 py-6">
      <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-serif text-4xl text-[var(--text)] mt-2 leading-none">
        {value}
      </div>
      {sublabel && (
        <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)] mt-3">
          {sublabel}
        </div>
      )}
    </div>
  );
}

export default function MetricsPage() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user.role === "admin") {
      Promise.all([
        adminApi.dashboardMetrics(),
        adminApi.dashboardOverview(),
      ])
        .then(([m, o]) => { setMetrics(m); setOverview(o); })
        .catch((e) => setErr(e.message || "Failed to load"));
    }
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/metrics" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/admin"
          data-testid="metrics-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Admin
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          Press Metrics
        </h1>
        <Link
          to="/investors"
          data-testid="link-investors"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
        >
          Investor Brief →
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 pb-20">
        <section className="py-16">
          <div className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[var(--accent)] mb-6">
            For press · safe to share
          </div>
          <h2
            data-testid="metrics-hero"
            className="font-serif text-4xl sm:text-5xl leading-[1.1] text-[var(--text)] max-w-2xl"
          >
            Humane Behavior AI by the numbers.
          </h2>
          <p className="font-sans text-sm text-[var(--text-muted)] mt-6 max-w-xl leading-relaxed">
            Live snapshot. No per-user data, no engine internals — only what
            we are comfortable making public.
          </p>
        </section>

        {err && (
          <div data-testid="metrics-error" className="border border-[var(--accent)] px-5 py-4 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[var(--accent)]">
            {err}
          </div>
        )}

        {!metrics && !err && (
          <div className="py-24 flex items-center justify-center gap-3 text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        )}

        {metrics && overview && (
          <div className="space-y-12 pb-10">
            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                Reach
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat label="Users" value={overview.users.total.toLocaleString()} testid="pr-users" />
                <Stat label="Conversations" value={overview.chats.total.toLocaleString()} testid="pr-chats" />
                <Stat label="MAU" value={metrics.activity.mau.toLocaleString()} sublabel="last 30 days" testid="pr-mau" />
              </div>
            </div>

            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                Corpus
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat label="Real-world cases" value={metrics.corpus.cases.toLocaleString()} testid="pr-corpus-cases" />
                <Stat label="Domains covered" value={metrics.corpus.domains} testid="pr-corpus-domains" />
                <Stat label="Source datasets" value={metrics.corpus.datasets} testid="pr-corpus-datasets" />
              </div>
            </div>

            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                Quality
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat
                  label="RAG hit rate"
                  value={`${(metrics.rag.hit_rate * 100).toFixed(1)}%`}
                  sublabel="cases surfaced on retrieval"
                  testid="pr-rag-rate"
                />
                <Stat label="Human evals" value={metrics.eval.votes.toLocaleString()} sublabel="side-by-side votes" testid="pr-eval-votes" />
                <Stat label="Eval runs" value={metrics.eval.runs.toLocaleString()} testid="pr-eval-runs" />
              </div>
            </div>

            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                Top domains queried
              </div>
              <div className="border border-[var(--border)] divide-y divide-[var(--border-soft)]">
                {metrics.top_domains.slice(0, 6).map((d) => (
                  <div
                    key={d.domain}
                    data-testid={`pr-domain-${d.domain}`}
                    className="flex items-center justify-between px-5 py-3"
                  >
                    <span className="font-ui text-[var(--text)] text-sm">{d.domain}</span>
                    <span className="font-mono-ui text-[11px] tracking-[0.18em] text-[var(--text-muted)]">
                      {d.count.toLocaleString()}
                    </span>
                  </div>
                ))}
                {metrics.top_domains.length === 0 && (
                  <p className="px-5 py-4 font-sans text-sm text-[var(--text-dim)]">
                    No queries yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
